/**
 * Product page and quick view behaviour, framework free:
 *
 * - <product-info>: wires the variant picker to the form, price, stock line,
 *   gallery and (on the product page) the URL and sticky add-to-cart bar.
 * - <product-gallery>: swipe rail on mobile with dots and a live status.
 * - Quantity steppers inside product forms.
 */
import { events } from '@theme/core';

class ProductInfo extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-product-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.idInput = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-button]');
    this.updateUrl = this.hasAttribute('data-update-url');

    this.addEventListener('change', (event) => {
      if (event.target.matches('.variant-picker__input')) this.#onOptionChange();
    });

    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-quantity-stepper] [data-step]');
      if (!step) return;
      const input = step.closest('[data-quantity-stepper]').querySelector('input');
      input.value = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.step));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.#setupStickyBar();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
  }

  get selectedOptions() {
    const groups = new Map();
    this.querySelectorAll('.variant-picker__input:checked').forEach((input) => {
      groups.set(Number(input.dataset.optionIndex), input.value);
    });
    return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, value]) => value);
  }

  #onOptionChange() {
    const options = this.selectedOptions;
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));

    options.forEach((value, i) => {
      const label = this.querySelector(`[data-option-value="${i}"]`);
      if (label) label.textContent = value;
    });

    this.#markAvailability(options);
    this.#render(variant);
    events.emit('variant:change', { variant, root: this });
  }

  /** Greys out values that would lead to an unavailable combination. */
  #markAvailability(selected) {
    this.querySelectorAll('.variant-picker__input').forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      const candidate = [...selected];
      candidate[index] = input.value;
      const match = this.variants.find((v) => v.options.every((value, i) => value === candidate[i]));
      input.nextElementSibling?.classList.toggle('variant-picker__chip--unavailable', !match || !match.available);
    });
  }

  #render(variant) {
    const button = this.addButton;

    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.labelUnavailable;
      }
      this.querySelectorAll('[data-sticky-add]').forEach((b) => (b.disabled = true));
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
    }
    this.querySelectorAll('[data-sticky-add]').forEach((b) => (b.disabled = !variant.available));

    // Price (server-formatted strings from the variant JSON)
    this.querySelectorAll('[data-price]').forEach((price) => {
      const current = price.querySelector('.price__current');
      if (current) current.textContent = variant.price;
      let compare = price.querySelector('.price__compare');
      if (variant.compare_at) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          price.append(compare);
        }
        compare.textContent = variant.compare_at;
        price.classList.add('price--sale');
      } else {
        compare?.remove();
        price.classList.remove('price--sale');
      }
    });

    // Stock line
    const stock = this.querySelector('[data-stock]');
    if (stock) {
      const label = stock.querySelector('[data-stock-label]');
      const low = Number(stock.dataset.lowThreshold) || 5;
      stock.classList.toggle('stock--out', !variant.available);
      if (!variant.available) label.textContent = stock.dataset.labelOut;
      else if (variant.inventory_tracked && variant.inventory_quantity <= low)
        label.textContent = stock.dataset.labelLow.replace('[count]', variant.inventory_quantity);
      else label.textContent = stock.dataset.labelIn;
    }

    // Gallery: jump to the variant's image
    if (variant.media_id) this.querySelector('product-gallery')?.select(variant.media_id);

    if (this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  /** Mobile sticky bar appears once the main Add to bag scrolls out of view. */
  #setupStickyBar() {
    const bar = this.querySelector('[data-sticky-bar]');
    const target = this.querySelector('[data-add-button]');
    if (!bar || !target) return;

    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => target.click());

    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.toggleAttribute('data-visible', passed);
        bar.inert = !passed;
      },
      { threshold: 0 },
    );
    this.stickyObserver.observe(target);
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.items = [...this.querySelectorAll('.gallery__item')];
    this.dots = [...this.querySelectorAll('.gallery__dot')];
    this.status = this.querySelector('[data-gallery-status]');
    if (!this.track || this.items.length < 2) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.#setActive(this.items.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: [0.6] },
    );
    this.items.forEach((item) => this.observer.observe(item));

    // Scroll the initially selected (variant) image into view in rail mode
    const selected = this.items.findIndex((item) => item.hasAttribute('data-selected'));
    if (selected > 0) this.#scrollTo(selected, 'instant');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get isRail() {
    return getComputedStyle(this.track).overflowX !== 'visible';
  }

  select(mediaId) {
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    this.items.forEach((item, i) => item.toggleAttribute('data-selected', i === index));
    if (this.isRail) {
      this.#scrollTo(index);
    } else if (index > 0) {
      // Grid: bring the variant image to the front
      this.track.prepend(this.items[index]);
      this.items = [...this.querySelectorAll('.gallery__item')];
      this.track.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  #scrollTo(index, behavior = 'smooth') {
    const item = this.items[index];
    const delta = item.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollBy({ left: delta, behavior });
  }

  #setActive(index) {
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (this.status) {
      this.status.textContent = this.status.dataset.template
        .replace('[current]', index + 1)
        .replace('[total]', this.items.length);
    }
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

/*
 * Back button: return to the exact previous page (keeping its scroll and
 * filters) when the visitor came from this store; otherwise follow the
 * link to the product's collection.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-back-link]');
  if (!link) return;
  let sameSite = false;
  try {
    sameSite = document.referrer && new URL(document.referrer).origin === window.location.origin;
  } catch {
    sameSite = false;
  }
  if (sameSite && window.history.length > 1) {
    event.preventDefault();
    window.history.back();
  }
});
